import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  IconButton,
  Chip,
} from '@material-ui/core';
import { Edit, Timeline } from '@material-ui/icons';

import SelectField from 'components/SelectField';
import SearchBar from 'components/SearchBar';
import useDebounce from 'hooks/useDebounce';
import PaginationData from 'types/models/PaginationData';
import AppContext from 'contexts/AppContext';
import EditStockModuleContext from 'contexts/EditStockModuleContext';
import { mapToSelectOptions } from 'utils/select';
import { getVenuesWithStockModuleActive } from 'services/venuesServices';
import { getGlobalProducts } from 'services/stockServices';
import { GlobalProduct, StockProductType } from 'types/models/StockReceipt';
import Venue from 'types/models/Venue';
import useSetupInitialState from 'hooks/useSetupInitialState';
import UserRoleType from 'types/enums/UserRoleType';
import { formatMoneyAuto } from 'utils/formatMoney';

const TYPE_LABEL: Record<StockProductType, string> = {
  [StockProductType.FINISHED]: 'stock.global-products.type-finished',
  [StockProductType.RAW_MATERIAL]: 'stock.global-products.type-raw-material',
};

export default function ListStockGlobalProductsPage(): JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const { authData, currentPageListNumber, setCurrentPageListNumber } = React.useContext(AppContext);
  const { setSelectedGlobalProduct } = React.useContext(EditStockModuleContext);
  const entity = useSetupInitialState();
  const isVenueEditor = authData?.user?.role === UserRoleType.VenueEditor;

  const [venues, setVenues] = React.useState<Venue[]>([]);
  const [selectedVenueIds, setSelectedVenueIds] = React.useState<string[]>([]);
  const [selectedType, setSelectedType] = React.useState<StockProductType | undefined>(undefined);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [isLoadingVenues, setIsLoadingVenues] = React.useState(true);
  const [paginationData, setPaginationData] = React.useState<PaginationData<GlobalProduct>>();

  const typeOptions = [
    { label: t('stock.global-products.type-finished'), value: StockProductType.FINISHED },
    { label: t('stock.global-products.type-raw-material'), value: StockProductType.RAW_MATERIAL },
  ];

  React.useEffect(() => {
    let mounted = true;

    async function loadVenues() {
      if (isVenueEditor) {
        return; // el venue del editor se resuelve desde entity
      }

      setIsLoadingVenues(true);

      try {
        const response = await getVenuesWithStockModuleActive();

        if (!mounted) return;

        setVenues(response?.data ?? []);
      } catch (error) {
        console.error(error);
      } finally {
        if (mounted) {
          setIsLoadingVenues(false);
        }
      }
    }

    loadVenues();

    return () => {
      mounted = false;
    };
  }, [isVenueEditor]);

  // Para venue editors el venue es único: se toma automáticamente al cargar la entidad
  React.useEffect(() => {
    if (!isVenueEditor) return;
    if (!entity?.id) return;

    setSelectedVenueIds([entity.id]);
    setIsLoadingVenues(false);
  }, [isVenueEditor, entity]);

  const loadData = React.useCallback(
    async (page?: number, search?: string, venueIds?: string[], type?: StockProductType) => {
      try {
        const data = await getGlobalProducts(page, search, venueIds, type);

        if (data) setPaginationData(data);
      } catch (error) {
        console.log(error);
      }
    },
    [],
  );

  useDebounce(() => {
    if (!selectedVenueIds.length) return;

    loadData(undefined, searchTerm || undefined, selectedVenueIds, selectedType);
  }, [searchTerm]);

  React.useEffect(() => {
    if (!selectedVenueIds.length) {
      setPaginationData(undefined);
      return;
    }

    loadData(currentPageListNumber ?? 1, searchTerm || undefined, selectedVenueIds, selectedType);
  }, [currentPageListNumber, loadData, selectedVenueIds, selectedType]);

  const handleVenuesChange = (venueIds: string[]) => {
    setSelectedVenueIds(venueIds);
    setCurrentPageListNumber(1);
  };

  const handleTypeChange = (value: string | null) => {
    setSelectedType((value as StockProductType | null) ?? undefined);
    setCurrentPageListNumber(1);
  };

  return (
    <>
      <SearchBar
        label={t('stock.global-products.search-placeholder')}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onClear={() => setSearchTerm('')}
      />

      {!isVenueEditor && (
        <SelectField
          name="venueIds"
          label={t('stock.forms.venue')}
          placeholder={t('stock.global-products.select-venues-placeholder')}
          value={selectedVenueIds}
          options={mapToSelectOptions(venues, { label: 'name' })}
          setFieldValue={(_, value) => handleVenuesChange(value)}
          touched={false}
          error={false}
          isMulti={false}
          isSearchable
          isClearable
          backspaceRemovesValue={false}
          onBlur={() => undefined}
          isDisabled={isLoadingVenues}
        />
      )}
      <SelectField
        name="type"
        label={t('stock.global-products.table.type')}
        placeholder={t('stock.global-products.select-type-placeholder')}
        value={selectedType}
        options={typeOptions}
        setFieldValue={(_, value) => handleTypeChange(value as string | null)}
        touched={false}
        error={false}
        isMulti={false}
        isSearchable={false}
        isClearable
        backspaceRemovesValue={false}
        onBlur={() => undefined}
        isDisabled={isLoadingVenues}
      />

      <Button
        className="btn-50 my-6"
        variant="contained"
        color="primary"
        onClick={() => history.push('/private/stock/global-products/create')}
      >
        {t('stock.global-products.create')}
      </Button>

      {selectedVenueIds.length > 0 && (
        <>
          <TableContainer className="table-container" component={Paper}>
            <Table>
              <TableHead className="table-header">
                <TableRow>
                  <TableCell>{t('stock.global-products.table.product')}</TableCell>
                  <TableCell>{t('stock.global-products.table.sku')}</TableCell>
                  <TableCell>{t('stock.global-products.average-cost')}</TableCell>
                  <TableCell>{t('stock.global-products.replacement-cost')}</TableCell>
                  <TableCell>{t('stock.global-products.table.type')}</TableCell>
                  <TableCell>{t('stock.global-products.table.unit-measure')}</TableCell>
                  <TableCell>{t('stock.global-products.table.reception-unit')}</TableCell>
                  <TableCell>{t('stock.global-products.table.units-per-reception-unit')}</TableCell>
                  <TableCell>{t('stock.global-products.table.minimum-stock')}</TableCell>
                  <TableCell>{t('stock.global-products.table.safety-stock')}</TableCell>
                  <TableCell>{t('stock.global-products.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginationData?.data && paginationData.data.length > 0 ? (
                  paginationData.data.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell>{product.name}</TableCell>
                      <TableCell className="unique-value-cell">{product.sku}</TableCell>
                      <TableCell>{formatMoneyAuto(product.averageCost)}</TableCell>
                      <TableCell>{formatMoneyAuto(product.replacementCost ?? 0)}</TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" label={t(TYPE_LABEL[product.type] ?? product.type)} />
                      </TableCell>
                      <TableCell>
                        {product.type === StockProductType.RAW_MATERIAL && product.unitMeasure
                          ? t(`stock.units.${product.unitMeasure}`)
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {product.type === StockProductType.RAW_MATERIAL && product.receptionUnitMeasure
                          ? t(`stock.units.${product.receptionUnitMeasure}`)
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {product.type === StockProductType.RAW_MATERIAL && product.unitsPerReceptionUnit != null
                          ? product.unitsPerReceptionUnit
                          : '-'}
                      </TableCell>
                      <TableCell>{product.minimumStock ?? '-'}</TableCell>
                      <TableCell>{product.safetyStock ?? '-'}</TableCell>
                      <TableCell>
                        <IconButton
                          color="inherit"
                          title={t('stock.global-products.table.view-movements')}
                          onClick={() => {
                            history.push(
                              `/private/stock/movements?globalProductId=${product.id}&productName=${encodeURIComponent(
                                product.name,
                              )}`,
                            );
                          }}
                        >
                          <Timeline />
                        </IconButton>
                        <IconButton
                          color="inherit"
                          onClick={() => {
                            setSelectedGlobalProduct(product);
                            history.push(`/private/stock/global-products/${product.id}/edit`);
                          }}
                        >
                          <Edit />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell component="th" scope="row">
                      {t('stock.global-products.table.no-results')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {paginationData && (
            <TablePagination
              component="div"
              count={paginationData.total}
              page={paginationData.currentPage - 1}
              onPageChange={(_, page: number) => {
                setCurrentPageListNumber(page + 1);
              }}
              rowsPerPage={paginationData.perPage}
              rowsPerPageOptions={[]}
              labelDisplayedRows={({ from, to, count }) => {
                return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
              }}
            />
          )}
        </>
      )}
    </>
  );
}
